// ═══════════════════════════════════════════════════════════════════════════════
// Transformer Model Registry for Stem Separation
// RoFormer-style architectures are the default; other HuggingFace transformers
// are available as alternatives for ensemble or specialist roles.
// ═══════════════════════════════════════════════════════════════════════════════

export type ModelArchitecture =
  | 'roformer'
  | 'band_split_roformer'
  | 'mel_band_roformer'
  | 'htdemucs'
  | 'mdx_net'
  | 'scnet'
  | 'segm_models';

export type ModelTier = 'production' | 'experimental' | 'research';
export type StemTarget = 'vocals' | 'drums' | 'bass' | 'guitar' | 'piano' | 'other' | 'instrumental';

export interface TransformerModel {
  id: string;
  name: string;
  architecture: ModelArchitecture;
  huggingface_ref: string | null;
  version: string;
  stems: StemTarget[];
  tier: ModelTier;
  default_for?: string;
  strengths: string[];
  weaknesses: string[];
  sdr_benchmark: Record<string, number>;
  params_millions: number;
  inference_speed: 'fast' | 'medium' | 'slow';
  memory_gb: number;
  description: string;
  rotary_embedding: boolean;
  attention_type: string;
  frequency_domain: boolean;
}

// ─── RoFormer Family (Default) ──────────────────────────────────────────────

const ROFORMER_MODELS: TransformerModel[] = [
  {
    id: 'bs_roformer_v2',
    name: 'BS-RoFormer v2',
    architecture: 'band_split_roformer',
    huggingface_ref: 'bs_roformer',
    version: '2.0.0',
    stems: ['vocals', 'drums', 'bass', 'other'],
    tier: 'production',
    default_for: 'general',
    strengths: ['highest-sdr-vocals', 'band-split-precision', 'rotary-position-encoding', 'multi-stem'],
    weaknesses: ['slower-inference', 'high-vram'],
    sdr_benchmark: { vocals: 10.87, drums: 9.24, bass: 8.91, other: 6.82 },
    params_millions: 178,
    inference_speed: 'medium',
    memory_gb: 6.2,
    description: 'Band-Split RoFormer with rotary positional embeddings. State-of-the-art vocal separation via frequency-band attention. Default model for all stemification tasks.',
    rotary_embedding: true,
    attention_type: 'band_split_multi_head',
    frequency_domain: true,
  },
  {
    id: 'mel_roformer_v2',
    name: 'Mel-RoFormer v2',
    architecture: 'mel_band_roformer',
    huggingface_ref: 'mel_band_roformer',
    version: '2.0.0',
    stems: ['vocals', 'instrumental'],
    tier: 'production',
    default_for: 'vocals_specialist',
    strengths: ['mel-scale-bands', 'artifact-free-vocals', 'reverb-handling', 'low-bleed'],
    weaknesses: ['2-stem-only', 'less-granular'],
    sdr_benchmark: { vocals: 10.54, instrumental: 9.83 },
    params_millions: 156,
    inference_speed: 'medium',
    memory_gb: 5.4,
    description: 'Mel-band RoFormer operating on perceptually-weighted frequency bands. Excels at clean vocal extraction with minimal artifacts in reverberant content.',
    rotary_embedding: true,
    attention_type: 'mel_band_attention',
    frequency_domain: true,
  },
  {
    id: 'bs_roformer_viperx',
    name: 'BS-RoFormer (ViperX)',
    architecture: 'band_split_roformer',
    huggingface_ref: 'bs_roformer',
    version: '1209',
    stems: ['vocals', 'instrumental'],
    tier: 'production',
    strengths: ['vocal-clarity', 'instrument-preservation', 'community-tuned'],
    weaknesses: ['2-stem-only', 'vocal-focused'],
    sdr_benchmark: { vocals: 10.72, instrumental: 9.61 },
    params_millions: 178,
    inference_speed: 'medium',
    memory_gb: 6.2,
    description: 'Community fine-tuned BS-RoFormer checkpoint optimized by ViperX. Known for exceptional vocal clarity while preserving instrumental detail.',
    rotary_embedding: true,
    attention_type: 'band_split_multi_head',
    frequency_domain: true,
  },
  {
    id: 'bs_roformer_denoise',
    name: 'BS-RoFormer (Denoise)',
    architecture: 'band_split_roformer',
    huggingface_ref: 'bs_roformer',
    version: 'denoise',
    stems: ['vocals', 'instrumental'],
    tier: 'experimental',
    strengths: ['noise-reduction', 'clean-output', 'speech-enhancement'],
    weaknesses: ['aggressive-filtering', 'may-remove-texture'],
    sdr_benchmark: { vocals: 10.31, instrumental: 9.12 },
    params_millions: 178,
    inference_speed: 'medium',
    memory_gb: 6.2,
    description: 'BS-RoFormer variant fine-tuned for noisy recordings. Aggressive denoise pre-filtering with vocal preservation. Ideal for live recordings or lo-fi sources.',
    rotary_embedding: true,
    attention_type: 'band_split_multi_head',
    frequency_domain: true,
  },
];

// ─── HTDemucs Family (Hybrid Transformer) ───────────────────────────────────

const HTDEMUCS_MODELS: TransformerModel[] = [
  {
    id: 'htdemucs_ft',
    name: 'HTDemucs Fine-Tuned',
    architecture: 'htdemucs',
    huggingface_ref: 'facebook/demucs',
    version: 'htdemucs_ft',
    stems: ['vocals', 'drums', 'bass', 'other'],
    tier: 'production',
    strengths: ['4-stem-balanced', 'low-artifact', 'well-tested', 'fast'],
    weaknesses: ['lower-sdr-than-roformer', 'no-guitar-piano-split'],
    sdr_benchmark: { vocals: 9.12, drums: 8.87, bass: 8.42, other: 5.94 },
    params_millions: 84,
    inference_speed: 'fast',
    memory_gb: 3.8,
    description: 'Hybrid Transformer Demucs with temporal and spectral attention. Reliable 4-stem workhorse with excellent balance across all stems.',
    rotary_embedding: false,
    attention_type: 'cross_domain_transformer',
    frequency_domain: false,
  },
  {
    id: 'htdemucs_6s',
    name: 'HTDemucs 6-Stem',
    architecture: 'htdemucs',
    huggingface_ref: 'facebook/demucs',
    version: 'htdemucs_6s',
    stems: ['vocals', 'drums', 'bass', 'guitar', 'piano', 'other'],
    tier: 'production',
    strengths: ['6-stem', 'guitar-separation', 'piano-isolation', 'granular'],
    weaknesses: ['slightly-lower-sdr', 'slower-than-4-stem'],
    sdr_benchmark: { vocals: 8.94, drums: 8.53, bass: 8.11, guitar: 6.72, piano: 6.31, other: 5.42 },
    params_millions: 97,
    inference_speed: 'medium',
    memory_gb: 4.6,
    description: '6-stem variant with dedicated guitar and piano streams. Best when individual instrument isolation is required for remixing or session-style output.',
    rotary_embedding: false,
    attention_type: 'cross_domain_transformer',
    frequency_domain: false,
  },
];

// ─── Alternative Architectures (HuggingFace Transformers) ────────────────────

const ALT_MODELS: TransformerModel[] = [
  {
    id: 'mdx23c',
    name: 'MDX23C (ConvTDFNet)',
    architecture: 'mdx_net',
    huggingface_ref: null,
    version: '23c',
    stems: ['vocals', 'instrumental'],
    tier: 'production',
    strengths: ['fastest-inference', 'instrumental-clarity', 'low-memory'],
    weaknesses: ['lower-vocal-sdr', '2-stem-only', 'no-position-encoding'],
    sdr_benchmark: { vocals: 9.87, instrumental: 9.34 },
    params_millions: 42,
    inference_speed: 'fast',
    memory_gb: 2.1,
    description: 'Convolution Time-Domain Frequency-Domain Net. Fastest inference with good quality. Ideal for real-time or batch workflows where speed is critical.',
    rotary_embedding: false,
    attention_type: 'tdf_attention',
    frequency_domain: true,
  },
  {
    id: 'scnet_xl',
    name: 'SCNet XL',
    architecture: 'scnet',
    huggingface_ref: null,
    version: 'xl',
    stems: ['vocals', 'drums', 'bass', 'other'],
    tier: 'experimental',
    strengths: ['drum-transients', 'bass-precision', 'skip-connections', 'spatial-audio'],
    weaknesses: ['vocal-artifacts', 'less-stable', 'experimental'],
    sdr_benchmark: { vocals: 9.21, drums: 9.56, bass: 9.12, other: 6.14 },
    params_millions: 124,
    inference_speed: 'medium',
    memory_gb: 5.1,
    description: 'Skip-Connected Network XL with dense residual paths. Excels at percussive and transient-heavy content. Best used as drum/bass specialist in ensemble.',
    rotary_embedding: false,
    attention_type: 'skip_connection_attention',
    frequency_domain: true,
  },
  {
    id: 'segm_models_mtl',
    name: 'SegM-Models MTL',
    architecture: 'segm_models',
    huggingface_ref: null,
    version: 'mtl_v1',
    stems: ['vocals', 'drums', 'bass', 'other'],
    tier: 'research',
    strengths: ['multi-task-learning', 'shared-representations', 'parameter-efficient'],
    weaknesses: ['research-grade', 'unstable-on-edge-cases'],
    sdr_benchmark: { vocals: 9.45, drums: 8.92, bass: 8.63, other: 6.01 },
    params_millions: 68,
    inference_speed: 'fast',
    memory_gb: 3.2,
    description: 'Multi-task learning separation model with shared backbone. Research-grade model exploring joint learning across stem types. Efficient but may exhibit instability.',
    rotary_embedding: false,
    attention_type: 'multi_task_cross_attention',
    frequency_domain: true,
  },
];

// ─── Full Registry ──────────────────────────────────────────────────────────

export const MODEL_REGISTRY: TransformerModel[] = [
  ...ROFORMER_MODELS,
  ...HTDEMUCS_MODELS,
  ...ALT_MODELS,
];

export const DEFAULT_MODEL_ID = 'bs_roformer_v2';

export function getModel(id: string): TransformerModel | undefined {
  return MODEL_REGISTRY.find((m) => m.id === id);
}

export function getDefaultModel(): TransformerModel {
  return MODEL_REGISTRY.find((m) => m.id === DEFAULT_MODEL_ID)!;
}

export function getModelsByArchitecture(arch: ModelArchitecture): TransformerModel[] {
  return MODEL_REGISTRY.filter((m) => m.architecture === arch);
}

export function getModelsByTier(tier: ModelTier): TransformerModel[] {
  return MODEL_REGISTRY.filter((m) => m.tier === tier);
}

export function getProductionModels(): TransformerModel[] {
  return MODEL_REGISTRY.filter((m) => m.tier === 'production');
}

export function getRoFormerModels(): TransformerModel[] {
  return MODEL_REGISTRY.filter((m) => m.rotary_embedding);
}

export function getModelsForStem(stem: StemTarget): TransformerModel[] {
  return MODEL_REGISTRY.filter((m) => m.stems.includes(stem));
}

export function rankModelsBySdr(stem: StemTarget): TransformerModel[] {
  return MODEL_REGISTRY
    .filter((m) => m.sdr_benchmark[stem] != null)
    .sort((a, b) => (b.sdr_benchmark[stem] ?? 0) - (a.sdr_benchmark[stem] ?? 0));
}

// Maps legacy model IDs to new registry IDs
export function resolveLegacyModel(legacyId: string): string {
  const map: Record<string, string> = {
    'htdemucs_6s': 'htdemucs_6s',
    'htdemucs': 'htdemucs_ft',
    'demucs_v4': 'htdemucs_ft',
    'bs_roformer': 'bs_roformer_v2',
    'mel_roformer': 'mel_roformer_v2',
    'mdx23c': 'mdx23c',
    'scnet_xl': 'scnet_xl',
  };
  return map[legacyId] ?? legacyId;
}

// Stored preference key
const STORAGE_KEY = 'pipes.stem_model';

export function getStoredModelPref(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_MODEL_ID;
  } catch {
    return DEFAULT_MODEL_ID;
  }
}

export function setStoredModelPref(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch { /* noop */ }
}
